import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { base } from "../../constants/apis";
import CookieService from "../../context/cookies";
import { MessageRes, Message } from "../../types/Messages";

export const messagesApi = createApi({
  reducerPath: "messagesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${base}/apis/vendor_contacts`,
    prepareHeaders: (headers) => {
      const token = CookieService.getCookie("jwtToken");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["messagesApi"],
  endpoints: (builder) => ({
    getMailMessages: builder.query<Message[], string | number>({
      query: (id) => ({
        url: "/get_mails.php",
        method: "POST",
        body: { vendor_contacts_id: id },
      }),
      providesTags: ["messagesApi"],
      transformResponse: (res: MessageRes) => res.data,
    }),
    sendMailMessage: builder.mutation({
      query: (payload) => ({
        url: "/send_mail.php",
        method: "POST",
        body: payload,
      }),

      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const isResend = Boolean(arg.existingId);
        const tempId = arg.existingId ?? Date.now();

        if (isResend) {
          // Mark existing failed message as "pending"
          dispatch(
            messagesApi.util.updateQueryData(
              "getMailMessages",
              arg.vendor_contacts_id,
              (draft) => {
                const msg = draft.find((m) => m.id === arg.existingId);
                if (msg) {
                  msg.status = "pending";
                }
              }
            )
          );
        } else {
          // New message optimistic insert
          dispatch(
            messagesApi.util.updateQueryData(
              "getMailMessages",
              arg.vendor_contacts_id,
              (draft) => {
                draft.push({
                  id: tempId,
                  message: arg.message,
                  mail_from: null,
                  status: "pending",
                } as any);
              }
            )
          );
        }

        try {
          const { data } = await queryFulfilled;

          if (data.status === "success") {
            // replace with real message + refetch if you want
            dispatch(
              messagesApi.util.updateQueryData(
                "getMailMessages",
                arg.vendor_contacts_id,
                (draft) => {
                  const msg = draft.find((m) => m.id === tempId);
                  if (msg) {
                    // msg.id = data.data.id;
                    msg.status = "sent";
                  }
                }
              )
            );

            // ✅ only refetch on success if you want the latest
            dispatch(
              messagesApi.util.invalidateTags([{ type: "messagesApi" }])
            );
          } else {
            // mark failed
            dispatch(
              messagesApi.util.updateQueryData(
                "getMailMessages",
                arg.vendor_contacts_id,
                (draft) => {
                  const msg = draft.find((m) => m.id === tempId);
                  if (msg) msg.status = "failed";
                }
              )
            );
          }
        } catch {
          dispatch(
            messagesApi.util.updateQueryData(
              "getMailMessages",
              arg.vendor_contacts_id,
              (draft) => {
                const msg = draft.find((m) => m.id === tempId);
                if (msg) {
                  msg.status = "failed";
                }
              }
            )
          );
        }
      },
    }),
  }),
});

export const { useSendMailMessageMutation, useGetMailMessagesQuery } =
  messagesApi;
